import * as THREE from '@phdsoft/three';
import {
    EdgeIndexArray,
    LIGHT_MODEL_EDGE_ANGLE_DEG,
    LightModelEdgesRequest,
    LightModelEdgesRequestItem,
    LightModelEdgesResponse,
} from './light-model-edges-geometry';

/** Name of the LineSegments child that draws a light-model mesh's crease edges. */
export const LIGHT_MODEL_EDGES_NAME = 'LightModelEdges';

/** True for the edges object of a light-model mesh (see createLightModelEdgesObject). */
export function isLightModelEdges(object: THREE.Object3D | null | undefined): boolean {
    return object?.userData?.isLightModelEdges === true;
}

/** Every Mesh under `root` (not InstancedMesh) that has a position attribute and no edges yet. */
export function collectLightModelMeshes(root: THREE.Object3D): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    root?.traverse(object => {
        const mesh = object as THREE.Mesh;
        if (!mesh.isMesh || (mesh as any).isInstancedMesh) return;
        if (!mesh.geometry?.getAttribute?.('position')) return;
        if (mesh.children.some(isLightModelEdges)) return;
        meshes.push(mesh);
    });
    return meshes;
}

/**
 * Copies of a geometry's positions (xyz, de-interleaved / de-normalised when needed) and index,
 * each in its own buffer so they can be transferred to the worker without detaching the live
 * geometry's arrays. Null when there is no triangle to process.
 */
export function extractEdgeSource(geometry: THREE.BufferGeometry | null | undefined): { position: Float32Array; index: EdgeIndexArray | null } | null {
    const attribute = geometry?.getAttribute?.('position') as THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
    if (!attribute || attribute.count < 3) return null;
    let position: Float32Array;
    const plain = !(attribute as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute
        && attribute.itemSize === 3
        && !attribute.normalized
        && attribute.array instanceof Float32Array;
    if (plain) {
        position = (attribute.array as Float32Array).slice(0, attribute.count * 3);
    } else {
        position = new Float32Array(attribute.count * 3);
        for (let i = 0; i < attribute.count; i++) {
            position[i * 3] = attribute.getX(i);
            position[i * 3 + 1] = attribute.getY(i);
            position[i * 3 + 2] = attribute.getZ(i);
        }
    }
    const indexAttribute = geometry.getIndex();
    const index = indexAttribute ? (indexAttribute.array as EdgeIndexArray).slice(0, indexAttribute.count) : null;
    return { position, index };
}

/**
 * The edges of one light-model mesh, added as a child of that mesh: positions are in the mesh's
 * local space and the lines inherit its visibility (region hidden from the explorer…). Never a
 * raycast target, so light-model picking only ever sees the meshes.
 */
export function createLightModelEdgesObject(positions: Float32Array, material: THREE.Material, visible: boolean): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const lines = new THREE.LineSegments(geometry, material);
    lines.name = LIGHT_MODEL_EDGES_NAME;
    lines.userData.isLightModelEdges = true;
    lines.raycast = () => { /* not pickable */ };
    lines.visible = visible;
    return lines;
}

export type LightModelEdgesWorkerFactory = () => Worker;

/**
 * Crease edges ("borders") for the light model, which ships without line objects. Computed in a
 * Web Worker (light-model-edges.worker.ts) after the light model is loaded, so neither the load
 * nor the first render waits: each mesh gets its LineSegments child when the answer arrives.
 * Visibility follows the "Show borders" setting (read at creation, then setVisible).
 * Without a worker, or if it fails, a warning is logged and no edges are drawn.
 */
export class LightModelEdges {
    private static nextJobId = 1;

    private worker: Worker | null = null;
    private disposed = false;
    private readonly lines: THREE.LineSegments[] = [];

    constructor(
        private readonly material: THREE.Material,
        private readonly createWorker: LightModelEdgesWorkerFactory,
        /** Current "Show borders" value, read when the edges are created. */
        private readonly isVisible: () => boolean,
        /** Called after edges were added (e.g. request a render). */
        private readonly onEdgesAdded?: () => void,
    ) { }

    /** Number of edge objects currently attached. */
    public get count(): number {
        return this.lines.length;
    }

    /**
     * Starts the edge build for every mesh under `root` (the light campaign group).
     * @returns true when a worker request was posted.
     */
    public build(root: THREE.Object3D): boolean {
        if (this.disposed || this.worker) return false;
        const meshes = collectLightModelMeshes(root);
        const items: LightModelEdgesRequestItem[] = [];
        const transfer: Transferable[] = [];
        meshes.forEach((mesh, id) => {
            const source = extractEdgeSource(mesh.geometry);
            if (!source) return;
            items.push({ id, position: source.position, index: source.index });
            transfer.push(source.position.buffer);
            if (source.index) transfer.push(source.index.buffer);
        });
        if (items.length === 0) return false;

        let worker: Worker;
        try {
            worker = this.createWorker();
        } catch (error) {
            console.warn('Light model edges: Web Worker unavailable; no edges drawn.', error);
            return false;
        }
        if (!worker) {
            console.warn('Light model edges: Web Worker unavailable; no edges drawn.');
            return false;
        }
        this.worker = worker;
        const jobId = LightModelEdges.nextJobId++;
        worker.onmessage = (event: MessageEvent<LightModelEdgesResponse>) => this.onResponse(jobId, meshes, event.data);
        worker.onerror = (event: any) => {
            console.warn('Light model edges: worker failed; no edges drawn.', event?.message ?? event);
            this.terminate();
        };
        const request: LightModelEdgesRequest = { jobId, thresholdAngleDeg: LIGHT_MODEL_EDGE_ANGLE_DEG, items };
        try {
            worker.postMessage(request, transfer);
        } catch (error) {
            console.warn('Light model edges: could not post to the worker; no edges drawn.', error);
            this.terminate();
            return false;
        }
        return true;
    }

    /** Shows or hides every edge object ("Show borders" toggle). */
    public setVisible(visible: boolean): void {
        for (const lines of this.lines) lines.visible = visible;
    }

    /** Terminates the worker, ignores any later answer, removes the edges and disposes their geometries. */
    public dispose(): void {
        this.disposed = true;
        this.terminate();
        for (const lines of this.lines) {
            lines.removeFromParent();
            lines.geometry?.dispose();
        }
        this.lines.length = 0;
    }

    private onResponse(jobId: number, meshes: THREE.Mesh[], response: LightModelEdgesResponse): void {
        this.terminate();
        if (this.disposed || !response || response.jobId !== jobId) return;
        const visible = this.isVisible();
        let added = 0;
        for (const result of response.results ?? []) {
            const mesh = meshes[result.id];
            // Gone from the light model (disposed / replaced) while the worker ran
            if (!mesh || !mesh.parent) continue;
            if (!result.positions || result.positions.length < 6) continue;
            if (mesh.children.some(isLightModelEdges)) continue;
            const lines = createLightModelEdgesObject(result.positions, this.material, visible);
            mesh.add(lines);
            this.lines.push(lines);
            added++;
        }
        if (added > 0) this.onEdgesAdded?.();
    }

    private terminate(): void {
        if (!this.worker) return;
        this.worker.onmessage = null;
        this.worker.onerror = null;
        this.worker.terminate();
        this.worker = null;
    }
}

/**
 * Starts the edges worker (same pattern as linesComputerHelper.ts; bundled through angular.json
 * webWorkerTsConfig = tsconfig.worker.json). Throws when Web Workers are unavailable.
 */
export function createLightModelEdgesWorker(): Worker {
    if (typeof Worker === 'undefined') throw new Error('Web Workers are not supported in this environment');
    return new Worker(new URL('./light-model-edges.worker.ts', import.meta.url), { type: 'module' });
}
